import { filterErrorsByField } from 'Automations/automationEditor/utils'
import { Box, Grid } from '@sequencehq/core-components'
import { FC, Fragment } from 'react'

import { ConditionPropertyEditorComponentProps } from 'Automations/nodeDefinitions/conditions'
import { InputSelectControlField } from 'Automations/nodeDefinitions/components'
import { SUB_OPERATOR_COLUMN_WIDTH } from '../conditionEditorLayout'
import { ProductSelector } from '../../../../switch/components/GroupEditor/inputs/components/ProductSelector'
import { usePriceAttributeOptions } from './priceAttributeExhaustion'
import {
  appendTestId,
  IsSubOperator,
  PriceAttributeField,
  ProductConditionValue,
  readIsSubOperator,
  readValue,
  usePrimaryOperatorState
} from './ProductPropertyEditor.helpers'

type PriceAttributeOperatorEditorProps =
  ConditionPropertyEditorComponentProps & {
    /** The field on the condition value the attribute select writes to. */
    field: PriceAttributeField
    options: Array<{ label: string; value: string }>
  }

/**
 * Editor shared by the PRODUCT operators that compare one attribute of a
 * product's prices — pricing model, billing frequency, billing type. Each
 * renders the same row: a product picker, the primary operator dropdown, then
 * a comparison against the picked attribute. Only the attribute's field name,
 * its options and the comparisons on offer differ, so they come in as props.
 */
export const PriceAttributeOperatorEditor: FC<
  PriceAttributeOperatorEditorProps
> = ({
  'data-testid': dataTestId,
  operator,
  value,
  onChange,
  operators,
  siblingConditions,
  validationErrors,
  focusOnInitialLoad,
  field,
  options
}) => {
  const conditionValue = readValue(value)
  const { productId, operator: subOperator } = conditionValue
  const effectiveSubOperator = readIsSubOperator(subOperator)
  const attribute = conditionValue[field]

  const {
    operatorOptions,
    primaryOperatorDisabled,
    onPrimaryOperatorChange,
    sameProductSiblings
  } = usePrimaryOperatorState({
    operator,
    productId,
    operators,
    siblingConditions,
    onChange
  })

  const { subOperatorOptions, attributeOptions } = usePriceAttributeOptions({
    field,
    subOperator: effectiveSubOperator,
    attribute,
    options,
    sameProductSiblings
  })

  const update = (next: {
    productId?: string
    operator?: IsSubOperator
    attribute?: string
  }) =>
    onChange({
      value: {
        productId: next.productId ?? productId ?? '',
        operator: next.operator ?? effectiveSubOperator,
        [field]: next.attribute ?? attribute
      } as ProductConditionValue
    })

  return (
    <Fragment>
      <Grid
        gridTemplateColumns="1fr 1fr"
        gap="8px"
        gridTemplateAreas='"product operator" "subRow subRow"'
      >
        <Box gridArea="product">
          <ProductSelector
            data-testid={appendTestId(dataTestId, 'product')}
            value={productId ?? ''}
            onChange={selected => update({ productId: String(selected) })}
            validationErrors={filterErrorsByField(
              validationErrors?.value,
              'productId'
            )}
            initiallyOpen={focusOnInitialLoad}
          />
        </Box>
        <Box gridArea="operator">
          <InputSelectControlField
            data-testid={appendTestId(dataTestId, 'operator')}
            placeholder="Select..."
            options={operatorOptions}
            value={operator ?? ''}
            disabled={primaryOperatorDisabled}
            onChange={onPrimaryOperatorChange}
            validationErrors={validationErrors?.operator}
          />
        </Box>
        {!!productId && (
          <Grid
            data-testid={appendTestId(dataTestId, 'subRow')}
            gridArea="subRow"
            gridTemplateColumns={`${SUB_OPERATOR_COLUMN_WIDTH} minmax(0, 1fr)`}
            columnGap="8px"
          >
            <InputSelectControlField
              data-testid={appendTestId(dataTestId, 'subOperator')}
              value={effectiveSubOperator ?? ''}
              onChange={selected =>
                update({ operator: selected as IsSubOperator })
              }
              options={subOperatorOptions}
              placeholder="Select..."
              validationErrors={filterErrorsByField(
                validationErrors?.value,
                'operator'
              )}
            />
            <InputSelectControlField
              data-testid={appendTestId(dataTestId, field)}
              value={attribute ?? ''}
              onChange={selected => update({ attribute: String(selected) })}
              options={attributeOptions}
              placeholder="Select..."
              validationErrors={filterErrorsByField(
                validationErrors?.value,
                field
              )}
            />
          </Grid>
        )}
      </Grid>
    </Fragment>
  )
}
